import { h, Fragment } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import MiniGraph from '../lineGraph/mini';
import ShotTarget from '../tv/shotTarget';
import { bulletMm } from '../../lib/pistolTargets';
import { formatSeconds } from '../../lib/shooterShots';
import {
    groupStats, paceStats, progressSeries, progressTrend,
    shotOrderDrift, paceVsHit, seriesTypeComparison, competitionSplit, warmupCurve, stabilityStats, records,
    heatmapCells, HEATMAP_MIN_SHOTS,
} from '../../lib/shotAnalysis';

const BAR_PX = 48;
const GRAPH_COLOR = '#0d6efd';
const ORDER_TARGET_PX = 120;

/**
 * Every analysis on the «Skudd» tab, in display order. `on` is the default
 * when the shooter has not chosen; the choice is kept per user in
 * user.settings[settingKey(key)] as 1 / 0 (PATCH /api/users settings.*).
 * `scope`: 'series' cards sit under the opened series type, 'program' cards
 * under the program's row of targets, 'target' toggles change the targets.
 */
export const ANALYSIS_CARDS = [
    { key: 'group', on: true, scope: 'series' },
    { key: 'pace', on: true, scope: 'series' },
    { key: 'progress', on: true, scope: 'series' },
    { key: 'order', on: false, scope: 'series' },
    { key: 'paceHit', on: false, scope: 'series' },
    { key: 'drift', on: false, scope: 'series' },
    { key: 'types', on: false, scope: 'program' },
    { key: 'competition', on: false, scope: 'series' },
    { key: 'warmup', on: false, scope: 'series' },
    { key: 'decimals', on: false, scope: 'series' },
    { key: 'stability', on: false, scope: 'series' },
    { key: 'records', on: false, scope: 'series' },
    { key: 'heatmap', on: false, scope: 'target' },
];

export function settingKey(key) {
    return `shotAnalysis_${key}`;
}

// { key: boolean } from the user's settings, defaults where nothing is stored.
export function enabledAnalyses(settings) {
    const out = {};
    ANALYSIS_CARDS.forEach(card => {
        const stored = settings ? settings[settingKey(card.key)] : undefined;
        out[card.key] = stored === undefined || stored === null || stored === '' ? card.on : parseInt(stored, 10) === 1;
    });
    return out;
}

export const CARD_TITLES = {
    group: <Text id='user.shotAnalysis-group-title'>Your group</Text>,
    pace: <Text id='user.shotAnalysis-pace-title'>Your pace</Text>,
    progress: <Text id='user.shotAnalysis-progress-title'>Your progress</Text>,
    order: <Text id='user.shotAnalysis-order-title'>Shot order</Text>,
    paceHit: <Text id='user.shotAnalysis-paceHit-title'>Pace against hit</Text>,
    drift: <Text id='user.shotAnalysis-drift-title'>Sideways and height over time</Text>,
    types: <Text id='user.shotAnalysis-types-title'>Series types</Text>,
    competition: <Text id='user.shotAnalysis-competition-title'>Training against competition</Text>,
    warmup: <Text id='user.shotAnalysis-warmup-title'>Warm-up</Text>,
    decimals: <Text id='user.shotAnalysis-decimals-title'>Tens and decimals</Text>,
    stability: <Text id='user.shotAnalysis-stability-title'>Stability</Text>,
    records: <Text id='user.shotAnalysis-records-title'>Records</Text>,
    heatmap: <Text id='user.shotAnalysis-heatmap-title'>Heatmap</Text>,
};

export function formatMm(value) {
    if (!Number.isFinite(value)) return '';
    return `${value.toFixed(1).replace('.', ',')} mm`;
}

function formatDecimal(value, digits = 2) {
    if (!Number.isFinite(value)) return '';
    return value.toFixed(digits).replace('.', ',');
}

function formatDate(date) {
    return date ? util.formatDate(date, { locale: 'nb-NO' }) : '';
}

// Bootstrap bars: one column per item, height from its share of the largest value.
function Bars({ items, className }) {
    const max = Math.max(...items.map(i => i.value), 0);
    return (
        <div class={`shot-bars d-flex align-items-end gap-1 ${className || ''}`} style={`height: ${BAR_PX + 30}px;`}>
            {items.map(item => (
                <div key={item.key} class='shot-bar d-flex flex-column align-items-center flex-fill' style='min-width: 0;'>
                    <small class='text-body-secondary' style='font-size: 0.6rem; line-height: 1;'>{item.text}</small>
                    <div
                        class={`bar rounded-top w-100 ${item.muted ? 'bg-body-secondary' : 'bg-primary'}`}
                        style={`height: ${max > 0 ? Math.max(1, Math.round((item.value / max) * BAR_PX)) : 1}px;`}
                        data-value={item.value}
                    />
                    <small class='text-body-secondary text-truncate mw-100' style='font-size: 0.6rem; line-height: 1.2;'>{item.label}</small>
                </div>
            ))}
        </div>
    );
}

function Card({ step, title, subtitle, children, className }) {
    return (
        <div class={`shot-analysis-card card flex-fill ${className || ''}`} style='flex-basis: 260px; min-width: 0;'>
            <div class='card-body p-2'>
                <div class='d-flex align-items-baseline gap-2 mb-1'>
                    <span class='badge rounded-pill bg-primary'>{step}</span>
                    <div>
                        <strong>{title}</strong>
                        <div class='text-body-secondary small'>{subtitle}</div>
                    </div>
                </div>
                {children}
            </div>
        </div>
    );
}

function Stat({ label, value, wide }) {
    return (
        <div class={`shot-stat ${wide ? 'col-12' : 'col-6'} mb-1`}>
            <div class='text-body-secondary' style='font-size: 0.65rem; line-height: 1.1;'>{label}</div>
            <div class='fw-semibold' style='font-variant-numeric: tabular-nums;'>{value}</div>
        </div>
    );
}

function Note({ children }) {
    return <div class='shot-analysis-note text-body-secondary small'>{children}</div>;
}

function Legend({ children }) {
    return <div class='text-body-secondary' style='font-size: 0.65rem;'>{children}</div>;
}

function offsetText(x, y) {
    const parts = [];
    if (Math.abs(x) >= 0.5) {
        parts.push(<span key='h'>{formatMm(Math.abs(x))} {x > 0 ? <Text id='user.shotAnalysis-right'>right</Text> : <Text id='user.shotAnalysis-left'>left</Text>}</span>);
    }
    if (Math.abs(y) >= 0.5) {
        parts.push(<span key='v'>{formatMm(Math.abs(y))} {y > 0 ? <Text id='user.shotAnalysis-high'>high</Text> : <Text id='user.shotAnalysis-low'>low</Text>}</span>);
    }
    if (parts.length === 0) return <Text id='user.shotAnalysis-centered'>centred</Text>;
    return <span class='shot-mpi'>{parts[0]}{parts.length > 1 ? ', ' : ''}{parts[1] || null}</span>;
}

function LagStamp({ date, count }) {
    return (
        <span class='text-body-secondary' style='font-size: 0.65rem;'>
            {formatDate(date)}
            {Number.isFinite(count) ? <> · <Text id='user.shotHistory-shots' fields={{ count }}>{count} shots</Text></> : null}
        </span>
    );
}

// A line of one value per Lag (oldest first) in the shared MiniGraph look.
function LagGraph({ points, pick, stores, height = 90 }) {
    const data = points.map((p, i) => ({ epoch: p.date ? Date.parse(p.date) : i, x: i, y: Math.round(pick(p) * 100) / 100 }));
    return (
        <div class='shot-lag-graph' style='overflow: hidden;'>
            <MiniGraph
                stores={stores}
                dataLeft={[data]}
                width={280}
                height={height}
                xTicksCnt={3}
                xTicksVal={val => formatDate(val)}
                paddingLeft={28}
                paddingTop={6}
                smoothLeft={[false]}
                gradientFillLeft={[GRAPH_COLOR]}
                gradientOpacity={0.15}
                inputStyleLeft={`stroke: ${GRAPH_COLOR} !important; stroke-width: 2px;`}
            />
        </div>
    );
}

// ---- the cards ----

function GroupCard({ step, stats }) {
    return (
        <Card step={step} title={CARD_TITLES.group} subtitle={<Text id='user.shotAnalysis-group-sub'>Tight or spread out?</Text>} className='shot-analysis-group'>
            <div class='row g-0 small'>
                <Stat label={<Text id='user.shotAnalysis-meanRadius'>Mean distance from the group centre</Text>} value={formatMm(stats.meanRadiusMm)} />
                <Stat label={<Text id='user.shotAnalysis-cep'>95 % of the shots within</Text>} value={formatMm(stats.cep95Mm)} />
                <Stat label={<Text id='user.shotAnalysis-extent'>Width × height</Text>} value={`${formatMm(stats.extentXMm)} × ${formatMm(stats.extentYMm)}`} />
                <Stat label={<Text id='user.shotAnalysis-sd'>Spread sideways / up-down</Text>} value={`${formatMm(stats.sdXMm)} / ${formatMm(stats.sdYMm)}`} />
                <Stat label={<Text id='user.shotAnalysis-mpi'>Point of impact</Text>} value={offsetText(stats.mpi.x, stats.mpi.y)} />
                <Stat
                    label={<Text id='user.shotAnalysis-gain-label'>Centred group</Text>}
                    value={stats.gain.delta > 0
                        ? <span class='shot-gain text-success'>+{stats.gain.delta} <Text id='user.shotAnalysis-gain'>points</Text></span>
                        : <span class='shot-gain'><Text id='user.shotAnalysis-gain-none'>no gain</Text></span>}
                />
            </div>
            <Legend>
                <Text id='user.shotAnalysis-histogram'>Ring values</Text>
                {Number.isFinite(stats.avg) && <span> · <Text id='user.shotAnalysis-avg'>avg</Text> {formatDecimal(stats.avg)}</span>}
            </Legend>
            <Bars
                className='shot-histogram'
                items={stats.histogram.map(h => ({
                    key: h.value, value: h.count, label: h.value, text: h.count > 0 ? h.count : '', muted: h.count === 0,
                }))}
            />
        </Card>
    );
}

function PaceCard({ step, pace }) {
    const trend = {
        faster: <Text id='user.shotAnalysis-pace-faster'>You shoot faster towards the end of the series.</Text>,
        slower: <Text id='user.shotAnalysis-pace-slower'>You take longer towards the end of the series.</Text>,
        steady: <Text id='user.shotAnalysis-pace-steady'>Even rhythm through the series.</Text>,
    };
    return (
        <Card step={step} title={CARD_TITLES.pace} subtitle={<Text id='user.shotAnalysis-pace-sub'>Do you speed up at the end?</Text>} className='shot-analysis-pace'>
            {!pace && <Note><Text id='user.shotAnalysis-pace-none'>No shot times on these Lag.</Text></Note>}
            {pace && <>
                <div class='row g-0 small'>
                    <Stat label={<Text id='user.shotAnalysis-pace-avg'>Average between shots</Text>} value={`${formatSeconds(pace.avgCentis)} s`} />
                    <Stat
                        label={<Text id='user.shotAnalysis-pace-halves'>First half / second half</Text>}
                        value={`${formatSeconds(pace.firstHalfCentis)} / ${formatSeconds(pace.secondHalfCentis)} s`}
                    />
                </div>
                <div class={`shot-pace-trend small ${pace.trend === 'steady' ? '' : 'fw-semibold'}`} data-trend={pace.trend}>{trend[pace.trend]}</div>
                <Legend><Text id='user.shotAnalysis-pace-bars'>Seconds since the previous shot, per shot</Text></Legend>
                <Bars
                    className='shot-pace-bars'
                    items={pace.positions.map(p => ({
                        key: p.index, value: p.avgCentis, label: p.index + 1, text: formatSeconds(p.avgCentis),
                    }))}
                />
            </>}
        </Card>
    );
}

function ProgressCard({ step, points, trend, stores }) {
    const fields = trend ? { latest: formatMm(trend.latest), earlier: formatMm(trend.earlier) } : {};
    const sentence = trend ? {
        better: <Text id='user.shotAnalysis-progress-better' fields={fields}>Your last Lag was tighter than your average: {fields.latest} against {fields.earlier}.</Text>,
        worse: <Text id='user.shotAnalysis-progress-worse' fields={fields}>Your last Lag was wider than your average: {fields.latest} against {fields.earlier}.</Text>,
        steady: <Text id='user.shotAnalysis-progress-steady' fields={fields}>Your last Lag was on par with your average: {fields.latest} against {fields.earlier}.</Text>,
    }[trend.direction] : null;
    const last = points[points.length - 1];
    return (
        <Card step={step} title={CARD_TITLES.progress} subtitle={<Text id='user.shotAnalysis-progress-sub'>Lag after Lag</Text>} className='shot-analysis-progress'>
            {points.length < 2 && <Note><Text id='user.shotAnalysis-progress-none'>At least two Lag with shots are needed.</Text></Note>}
            {points.length >= 2 && <>
                <div class={`shot-progress-trend small ${trend && trend.direction !== 'steady' ? 'fw-semibold' : ''}`} data-trend={trend ? trend.direction : ''}>{sentence}</div>
                <Legend><Text id='user.shotAnalysis-progress-legend'>Mean distance from the group centre (mm) per Lag — lower is better</Text></Legend>
                <div class='shot-progress-graph'><LagGraph points={points} pick={p => p.meanRadiusMm} stores={stores} /></div>
                <div class='shot-progress-last'><LagStamp date={last.date} count={last.count} /></div>
            </>}
        </Card>
    );
}

function OrderCard({ step, drift, target, type }) {
    return (
        <Card step={step} title={CARD_TITLES.order} subtitle={<Text id='user.shotAnalysis-order-sub'>Where does shot 1, 2, 3 … land on average?</Text>} className='shot-analysis-order'>
            {!drift && <Note><Text id='user.shotAnalysis-order-none'>At least two Lag with shots in the same position are needed.</Text></Note>}
            {drift && (
                <div class='d-flex gap-2 align-items-start'>
                    <div style={`flex: 0 0 ${ORDER_TARGET_PX}px;`}>
                        <ShotTarget
                            target={target}
                            shots={drift.map(p => ({ x: Math.round(p.mpi.x * 10), y: Math.round(p.mpi.y * 10), countStatus: 'counted' }))}
                            bulletMm={bulletMm(type)}
                            size={`${ORDER_TARGET_PX}px`}
                        />
                    </div>
                    <div class='flex-grow-1 small' style='min-width: 0; font-variant-numeric: tabular-nums;'>
                        {drift.map(p => (
                            <div key={p.index} class='shot-order-row d-flex justify-content-between gap-2'>
                                <span class='text-body-secondary'><Text id='user.shotAnalysis-order-shot' fields={{ index: p.index }}>Shot {p.index}</Text></span>
                                <span>{offsetText(p.mpi.x, p.mpi.y)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
}

function Scatter({ points }) {
    const maxSplit = Math.max(...points.map(p => p.splitCentis), 1);
    const maxRadius = Math.max(...points.map(p => p.radiusMm), 1);
    const w = 100;
    const hgt = 60;
    return (
        <svg class='shot-scatter w-100' viewBox={`0 0 ${w} ${hgt}`} style='height: 90px; display: block;' preserveAspectRatio='none'>
            <line x1='0' y1={hgt - 0.5} x2={w} y2={hgt - 0.5} stroke='currentColor' stroke-width='0.5' opacity='0.3' />
            <line x1='0.5' y1='0' x2='0.5' y2={hgt} stroke='currentColor' stroke-width='0.5' opacity='0.3' />
            {points.map((p, i) => (
                <circle
                    key={i}
                    class='scatter-dot'
                    cx={(p.splitCentis / maxSplit) * (w - 4) + 2}
                    cy={hgt - 2 - (p.radiusMm / maxRadius) * (hgt - 4)}
                    r='1.6'
                    fill={GRAPH_COLOR}
                    opacity='0.7'
                />
            ))}
        </svg>
    );
}

function PaceHitCard({ step, result }) {
    const sentence = result ? {
        fastCloser: <Text id='user.shotAnalysis-paceHit-fastCloser'>Your quick shots land closer to the centre than the slow ones.</Text>,
        slowCloser: <Text id='user.shotAnalysis-paceHit-slowCloser'>Your slow shots land closer to the centre than the quick ones.</Text>,
        none: <Text id='user.shotAnalysis-paceHit-none'>No clear link between pace and hit.</Text>,
    }[result.link] : null;
    return (
        <Card step={step} title={CARD_TITLES.paceHit} subtitle={<Text id='user.shotAnalysis-paceHit-sub'>Do my quick shots miss?</Text>} className='shot-analysis-paceHit'>
            {!result && <Note><Text id='user.shotAnalysis-paceHit-few'>Too few timed shots yet.</Text></Note>}
            {result && <>
                <div class={`shot-paceHit-link small ${result.link === 'none' ? '' : 'fw-semibold'}`} data-link={result.link}>{sentence}</div>
                <div class='row g-0 small'>
                    <Stat label={<Text id='user.shotAnalysis-paceHit-fast'>Quick shots, mean distance</Text>} value={formatMm(result.fastAvgMm)} />
                    <Stat label={<Text id='user.shotAnalysis-paceHit-slow'>Slow shots, mean distance</Text>} value={formatMm(result.slowAvgMm)} />
                </div>
                <Legend><Text id='user.shotAnalysis-paceHit-legend'>Seconds since the previous shot (→) against distance from the group centre (↑)</Text></Legend>
                <Scatter points={result.points} />
            </>}
        </Card>
    );
}

function DriftCard({ step, points, stores }) {
    const last = points[points.length - 1];
    return (
        <Card step={step} title={CARD_TITLES.drift} subtitle={<Text id='user.shotAnalysis-drift-sub'>Point of impact per Lag</Text>} className='shot-analysis-drift'>
            {points.length < 2 && <Note><Text id='user.shotAnalysis-progress-none'>At least two Lag with shots are needed.</Text></Note>}
            {points.length >= 2 && <>
                <div class='small'><Text id='user.shotAnalysis-drift-last'>Last Lag</Text>: {offsetText(last.mpiX, last.mpiY)}</div>
                <Legend><Text id='user.shotAnalysis-drift-x'>Sideways (mm, + is right)</Text></Legend>
                <div class='shot-drift-x'><LagGraph points={points} pick={p => p.mpiX} stores={stores} height={60} /></div>
                <Legend><Text id='user.shotAnalysis-drift-y'>Height (mm, + is high)</Text></Legend>
                <div class='shot-drift-y'><LagGraph points={points} pick={p => p.mpiY} stores={stores} height={60} /></div>
            </>}
        </Card>
    );
}

function CompetitionCard({ step, split }) {
    const side = (label, stats) => (
        <div class='col-6'>
            <div class='fw-semibold small'>{label}</div>
            {!stats && <Note><Text id='user.shotAnalysis-competition-none'>No Lag</Text></Note>}
            {stats && <>
                <Legend><Text id='user.shotHistory-lags' fields={{ count: stats.lagCount }}>{stats.lagCount} Lag</Text> · <Text id='user.shotHistory-shots' fields={{ count: stats.count }}>{stats.count} shots</Text></Legend>
                <div class='row g-0 small'>
                    <Stat wide label={<Text id='user.shotAnalysis-meanRadius'>Mean distance from the group centre</Text>} value={formatMm(stats.meanRadiusMm)} />
                    <Stat wide label={<Text id='user.shotAnalysis-cep'>95 % of the shots within</Text>} value={formatMm(stats.cep95Mm)} />
                    <Stat wide label={<Text id='user.shotAnalysis-mpi'>Point of impact</Text>} value={offsetText(stats.mpi.x, stats.mpi.y)} />
                </div>
            </>}
        </div>
    );
    return (
        <Card step={step} title={CARD_TITLES.competition} subtitle={<Text id='user.shotAnalysis-competition-sub'>Nerves in numbers</Text>} className='shot-analysis-competition'>
            {!split && <Note><Text id='user.shotAnalysis-progress-none'>At least two Lag with shots are needed.</Text></Note>}
            {split && (
                <div class='row g-2'>
                    {side(<Text id='user.shotAnalysis-competition-training'>Training</Text>, split.training)}
                    {side(<Text id='user.shotAnalysis-competition-competition'>Competition</Text>, split.competition)}
                </div>
            )}
        </Card>
    );
}

function WarmupCard({ step, curve }) {
    return (
        <Card step={step} title={CARD_TITLES.warmup} subtitle={<Text id='user.shotAnalysis-warmup-sub'>Lag 1, 2, 3 on the same day</Text>} className='shot-analysis-warmup'>
            {!curve && <Note><Text id='user.shotAnalysis-warmup-none'>No day with two Lag yet.</Text></Note>}
            {curve && <>
                <Legend><Text id='user.shotAnalysis-warmup-legend'>Mean distance from the group centre (mm) by Lag number of the day — lower is better</Text></Legend>
                <Bars
                    className='shot-warmup-bars'
                    items={curve.map(c => ({
                        key: c.position, value: c.meanRadiusMm, label: `${c.position}`, text: formatMm(c.meanRadiusMm),
                    }))}
                />
                <Legend>
                    {curve.map((c, i) => (
                        <span key={c.position}>{i > 0 ? ' · ' : ''}{c.position}: <Text id='user.shotAnalysis-warmup-days' fields={{ count: c.days }}>{c.days} days</Text></span>
                    ))}
                </Legend>
            </>}
        </Card>
    );
}

function DecimalsCard({ step, points, stores }) {
    const withDecimals = points.filter(p => Number.isFinite(p.decimalAvg));
    const last = withDecimals[withDecimals.length - 1];
    const flagged = points.filter(p => Number.isFinite(p.innerTenShare));
    const share = flagged.length ? flagged.reduce((acc, p) => acc + p.innerTenShare * p.count, 0) / flagged.reduce((acc, p) => acc + p.count, 0) : null;
    return (
        <Card step={step} title={CARD_TITLES.decimals} subtitle={<Text id='user.shotAnalysis-decimals-sub'>Finer than the ring</Text>} className='shot-analysis-decimals'>
            {withDecimals.length === 0 && <Note><Text id='user.shotAnalysis-decimals-none'>No decimal values on these Lag.</Text></Note>}
            {withDecimals.length > 0 && <>
                <div class='row g-0 small'>
                    <Stat label={<Text id='user.shotAnalysis-decimals-last'>Last Lag, decimal average</Text>} value={formatDecimal(last.decimalAvg)} />
                    <Stat
                        label={<Text id='user.shotAnalysis-decimals-innerTen'>Inner tens</Text>}
                        value={Number.isFinite(share) ? `${Math.round(share * 100)} %` : '–'}
                    />
                </div>
                {withDecimals.length >= 2 && <>
                    <Legend><Text id='user.shotAnalysis-decimals-legend'>Decimal average per Lag</Text></Legend>
                    <div class='shot-decimals-graph'><LagGraph points={withDecimals} pick={p => p.decimalAvg} stores={stores} /></div>
                </>}
            </>}
        </Card>
    );
}

function StabilityCard({ step, stats }) {
    const sentence = stats ? {
        stable: <Text id='user.shotAnalysis-stability-stable'>Stable: your Lag totals stay close together.</Text>,
        varying: <Text id='user.shotAnalysis-stability-varying'>Varying: your Lag totals swing from Lag to Lag.</Text>,
    }[stats.label] : null;
    return (
        <Card step={step} title={CARD_TITLES.stability} subtitle={<Text id='user.shotAnalysis-stability-sub'>Lag totals</Text>} className='shot-analysis-stability'>
            {!stats && <Note><Text id='user.shotAnalysis-stability-none'>At least two Lag with the same shot count are needed.</Text></Note>}
            {stats && <>
                <div class={`shot-stability-label small ${stats.label === 'stable' ? 'text-success' : ''} fw-semibold`} data-label={stats.label}>{sentence}</div>
                <div class='row g-0 small'>
                    <Stat label={<Text id='user.shotAnalysis-stability-mean'>Average total</Text>} value={formatDecimal(stats.mean, 1)} />
                    <Stat label={<Text id='user.shotAnalysis-stability-sd'>Standard deviation</Text>} value={formatDecimal(stats.sd, 1)} />
                    <Stat label={<Text id='user.shotAnalysis-stability-range'>Lowest – highest</Text>} value={`${stats.min} – ${stats.max}`} />
                    <Stat label={<Text id='user.shotAnalysis-stability-lags'>Lag counted</Text>} value={<span>{stats.lagCount} <Text id='user.shotAnalysis-stability-of' fields={{ count: stats.count }}>({stats.count} shots each)</Text></span>} />
                </div>
            </>}
        </Card>
    );
}

function RecordsCard({ step, result }) {
    const row = (label, value, rec) => (
        <div class='shot-record d-flex justify-content-between align-items-baseline gap-2 small border-bottom py-1'>
            <span class='text-body-secondary'>{label}</span>
            <span class='text-end'>
                <span class='fw-semibold' style='font-variant-numeric: tabular-nums;'>{rec ? value : '–'}</span>
                {rec && <div><LagStamp date={rec.date} /></div>}
            </span>
        </div>
    );
    return (
        <Card step={step} title={CARD_TITLES.records} subtitle={<Text id='user.shotAnalysis-records-sub'>Your best so far</Text>} className='shot-analysis-records'>
            {!result && <Note><Text id='user.shotAnalysis-records-none'>No Lag yet.</Text></Note>}
            {result && <>
                {row(<Text id='user.shotAnalysis-records-lag'>Best Lag</Text>, result.bestLag ? `${result.bestLag.total} (${result.bestLag.count})` : '', result.bestLag)}
                {row(<Text id='user.shotAnalysis-records-group'>Best series</Text>, result.bestGroup ? `${result.bestGroup.total} (${result.bestGroup.count})` : '', result.bestGroup)}
                {row(<Text id='user.shotAnalysis-records-tens'>Longest run of tens</Text>, result.longestTenRun ? `${result.longestTenRun.length}` : '', result.longestTenRun)}
                {row(<Text id='user.shotAnalysis-records-decimal'>Best decimal average</Text>, result.bestDecimalAvg ? formatDecimal(result.bestDecimalAvg.value) : '', result.bestDecimalAvg)}
            </>}
        </Card>
    );
}

/**
 * The analysis cards under an opened series type on the profile «Skudd» tab.
 * `enabled` is enabledAnalyses(user.settings); only the enabled series-scope
 * cards render, numbered in display order. All numbers come from
 * lib/shotAnalysis.js over the series built by lib/shotHistory.js.
 */
export default function ShotAnalysis({
    series, type, stores, enabled,
}) {
    const on = enabled || enabledAnalyses(null);
    const stats = groupStats(series.shots, series.target);
    if (!stats) return null;
    const points = progressSeries(series.lags, series.target);
    const cards = [];
    let step = 0;
    const push = (key, render) => {
        if (!on[key]) return;
        step += 1;
        cards.push(render(step));
    };
    push('group', s => <GroupCard key='group' step={s} stats={stats} />);
    push('pace', s => <PaceCard key='pace' step={s} pace={paceStats(series.lags)} />);
    push('progress', s => <ProgressCard key='progress' step={s} points={points} trend={progressTrend(points)} stores={stores} />);
    push('order', s => <OrderCard key='order' step={s} drift={shotOrderDrift(series.lags)} target={series.target} type={type} />);
    push('paceHit', s => <PaceHitCard key='paceHit' step={s} result={paceVsHit(series.lags)} />);
    push('drift', s => <DriftCard key='drift' step={s} points={points} stores={stores} />);
    push('competition', s => <CompetitionCard key='competition' step={s} split={points.length >= 2 ? competitionSplit(series.lags, series.target) : null} />);
    push('warmup', s => <WarmupCard key='warmup' step={s} curve={warmupCurve(series.lags, series.target)} />);
    push('decimals', s => <DecimalsCard key='decimals' step={s} points={points} stores={stores} />);
    push('stability', s => <StabilityCard key='stability' step={s} stats={stabilityStats(points)} />);
    push('records', s => <RecordsCard key='records' step={s} result={records(series.lags)} />);
    if (cards.length === 0) return null;
    return <div class='shot-analysis d-flex flex-wrap gap-2 mt-2'>{cards}</div>;
}

// The program-scope card: mean radius per series type of the program.
export function ProgramAnalysis({ program }) {
    const rows = seriesTypeComparison(program);
    if (rows.length < 2) return null;
    return (
        <div class='shot-program-analysis d-flex flex-wrap gap-2 mt-2'>
            <Card step={<i class='fa-duotone fa-layer-group' />} title={CARD_TITLES.types} subtitle={<Text id='user.shotAnalysis-types-sub'>Which one to train?</Text>} className='shot-analysis-types'>
                <Legend><Text id='user.shotAnalysis-types-legend'>Mean distance from the group centre (mm) per series type — lower is better</Text></Legend>
                <Bars
                    className='shot-types-bars'
                    items={rows.map(r => ({
                        key: r.key, value: r.meanRadiusMm, label: r.label, text: formatMm(r.meanRadiusMm),
                    }))}
                />
            </Card>
        </div>
    );
}

// Overlays for the series target: the group ellipse and the CEP 95 circle.
export function seriesOverlays(series) {
    const stats = groupStats(series.shots, series.target);
    if (!stats) return null;
    return {
        ellipse: stats.ellipse,
        circle: stats.count >= 2 ? { cx: stats.mpi.x, cy: stats.mpi.y, r: stats.cep95Mm } : null,
    };
}

// The density heatmap for a series target, or null under HEATMAP_MIN_SHOTS shots.
export function seriesHeat(series) {
    if (!series || !Array.isArray(series.shots) || series.shots.length < HEATMAP_MIN_SHOTS) return null;
    return heatmapCells(series.shots, series.target);
}
